/**
 * Homepage description split.
 * Cut after the score section that contains `fbros-score--two`
 * (the </section> wrapping the second fbros-grid--3 of point cards).
 * To move the cut, change MARKER. A missing marker shows the full article.
 */
const MARKER = 'fbros-score--two';
const SECTION_END = '</section>';

export interface HomeDescriptionParts {
  visible: string;
  hidden: string;
}

export function splitHomeDescription(html: string): HomeDescriptionParts | null {
  const markerAt = html.indexOf(MARKER);
  if (markerAt === -1) {
    console.warn('[fbros-more] Marker not found; showing the full description.');
    return null;
  }

  const sectionEnd = html.indexOf(SECTION_END, markerAt);
  if (sectionEnd === -1) {
    console.warn('[fbros-more] No section close after the marker; showing the full description.');
    return null;
  }

  const cut = sectionEnd + SECTION_END.length;
  const hidden = html.slice(cut).trim();
  if (!hidden) {
    console.warn('[fbros-more] Nothing follows the score section; showing the full description.');
    return null;
  }

  return {
    visible: html.slice(0, cut),
    hidden: html.slice(cut),
  };
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Expand the homepage article before a hash target or an FAQ <details>
 * inside the collapsed region is revealed.
 */
export function bindFbrosMoreAnchors(root: HTMLElement, expand: () => void): () => void {
  const hidden = root.querySelector<HTMLElement>('.fbros-more__hidden');
  if (!hidden) return () => {};

  const isCollapsed = () => root.dataset.expanded !== 'true';

  const reveal = (target?: HTMLElement) => {
    if (!isCollapsed()) return;
    expand();
    if (!target) return;
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    window.requestAnimationFrame(() => {
      target.scrollIntoView({ block: 'start', behavior });
    });
  };

  const onClick = (event: MouseEvent) => {
    const source = event.target instanceof Element ? event.target : null;
    if (!source) return;

    const link = source.closest<HTMLAnchorElement>('a[href^="#"]');
    if (link) {
      const id = decodeURIComponent(link.getAttribute('href')?.slice(1) ?? '');
      const dest = id ? document.getElementById(id) : null;
      if (dest && hidden.contains(dest)) {
        reveal(dest);
      }
    }

    const summary = source.closest('summary');
    const details = summary?.parentElement;
    if (details instanceof HTMLDetailsElement && hidden.contains(details)) {
      reveal();
    }
  };

  const onToggle = (event: Event) => {
    const details = event.target;
    if (
      details instanceof HTMLDetailsElement &&
      details.open &&
      hidden.contains(details)
    ) {
      reveal();
    }
  };

  const onHash = () => {
    const id = decodeURIComponent(window.location.hash.replace(/^#/, ''));
    if (!id) return;
    const dest = document.getElementById(id);
    if (dest && hidden.contains(dest)) reveal(dest);
  };

  document.addEventListener('click', onClick);
  hidden.addEventListener('toggle', onToggle, true);
  window.addEventListener('hashchange', onHash);
  onHash();

  return () => {
    document.removeEventListener('click', onClick);
    hidden.removeEventListener('toggle', onToggle, true);
    window.removeEventListener('hashchange', onHash);
  };
}
